'use client'

import { Button } from '@/components/ui/button'
import SvgIcon from '@/components/ui/svg-icon'
import { Address } from '@/types/auth'
import { KolProfile } from '@/types/campaign'
import { ArrowLeft, MapPin, Phone, User } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { CampaignAddressBookModal } from './campaign-address-book-modal'
import { CampaignProductSummaryList } from './campaign-product-summary-list'

export interface ConfirmProductItem {
  readonly productId: string
  readonly name: string
  readonly image: string
  readonly color: string
  readonly size: string
  readonly price: number
  // Chỉ có ở màn chọn sản phẩm (dùng để xoá đúng dòng) - màn xác nhận/
  // thành công không cần vì không cho xoá.
  readonly rowId?: string
}

interface CampaignRegisterConfirmProps {
  kolProfile: KolProfile
  items: readonly ConfirmProductItem[]
  isSubmitting: boolean
  onBack: () => void
  onConfirm: () => void
  onEditAddress: () => void
  onProfileUpdate: (profile: KolProfile) => void
}

export const CampaignRegisterConfirm = ({
  kolProfile,
  items,
  isSubmitting,
  onBack,
  onConfirm,
  onEditAddress,
  onProfileUpdate,
}: CampaignRegisterConfirmProps) => {
  const t = useTranslations('community_threads.campaign.register_confirm')
  const [isAddressBookOpen, setIsAddressBookOpen] = useState(false)
  const fullAddress = [
    kolProfile.address,
    kolProfile.ward,
    kolProfile.district,
    kolProfile.city,
  ]
    .filter(Boolean)
    .join(', ')

  const handleSelectFromAddressBook = (address: Address) => {
    onProfileUpdate({
      ...kolProfile,
      fullName: address.fullname || kolProfile.fullName,
      phone: address.phone || kolProfile.phone,
      address: address.address || '',
      city: address.city ?? '',
      district: address.district ?? '',
      ward: address.ward ?? '',
    })
  }

  return (
    <div className='flex max-h-[90dvh] flex-col gap-6 overflow-hidden pt-6 md:pb-6 lg:max-h-[85dvh]'>
      <div className='flex shrink-0 items-center gap-2 px-5 lg:px-8'>
        <button
          type='button'
          onClick={onBack}
          aria-label={t('back_aria')}
          className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-neutral-100'
        >
          <ArrowLeft size={20} />
        </button>
        <h3 className='font-criteria text-2xl font-bold text-neutral-900'>
          {t('title')}
        </h3>
      </div>

      <div className='no-scrollbar min-h-0 flex-1 space-y-6 overflow-y-auto px-5 lg:px-8'>
        <div className='rounded-lg border border-neutral-200 bg-neutral-50 p-4'>
          <div className='mb-3 flex items-center justify-between gap-2'>
            <h5 className='font-criteria text-base font-bold text-neutral-900'>
              {t('shipping_info_title')}
            </h5>
            <button
              type='button'
              onClick={() => setIsAddressBookOpen(true)}
              className='flex shrink-0 items-center gap-1 text-sm font-medium text-primary hover:opacity-80'
            >
              <SvgIcon
                src='/icons/icon-address-book.svg'
                width={14}
                height={16}
              />
              {t('select_from_address_book')}
            </button>
          </div>
          <div className='space-y-2 text-sm text-neutral-700'>
            <div className='flex items-start gap-2'>
              <User size={16} className='mt-0.5 shrink-0' />
              <span>{kolProfile.fullName}</span>
            </div>
            <div className='flex items-start gap-2'>
              <Phone size={16} className='mt-0.5 shrink-0' />
              <span>{kolProfile.phone}</span>
            </div>
            <div className='flex items-start gap-2'>
              <MapPin size={16} className='mt-0.5 shrink-0' />
              <span>{fullAddress}</span>
            </div>
          </div>
          <Button
            type='button'
            variant='outline-neutral'
            size='small'
            className='mt-3'
            onClick={onEditAddress}
          >
            {t('update_info')}
          </Button>
        </div>

        <div>
          <h5 className='mb-3 font-criteria text-base font-bold text-neutral-900 md:text-xl'>
            {t('products_title', { count: items.length })}
          </h5>
          <CampaignProductSummaryList
            title={t('products_column_label')}
            items={items}
          />
        </div>
      </div>

      <div className='h-px w-full shrink-0 bg-neutral-200' />

      {/* Footer luôn cố định trong khung nhìn thực (flex-col + max-h-[90dvh]
      trên div cha, shrink-0 ở đây) - vùng nội dung phía trên tự co lại và tự
      cuộn để nhường chỗ, thay vì phụ thuộc vào 1 con số vh cố định. */}
      <div className='flex shrink-0 gap-3 px-5 pb-2 lg:px-8 lg:pb-0'>
        <Button
          type='button'
          variant='outline-neutral'
          className='flex-1'
          onClick={onBack}
          disabled={isSubmitting}
        >
          {t('back')}
        </Button>
        <Button
          type='button'
          variant='black'
          className='flex-1'
          loading={isSubmitting}
          onClick={onConfirm}
        >
          {t('confirm')}
        </Button>
      </div>

      <CampaignAddressBookModal
        open={isAddressBookOpen}
        onOpenChange={setIsAddressBookOpen}
        onSelectAddress={handleSelectFromAddressBook}
      />
    </div>
  )
}
